import type { CommunityChannelMessageSearchItem } from '@ps/api/types';
import type {
  CommunityChannelMemberProfile,
  CommunityChannelMessageContent,
  CommunityChannelMessageMention,
} from '@ps/models/lib/community-channel';

import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import { toAppDate } from '../utils/appDate';

/**
 * Adapter: search-API message item → the unified {@link ExtendedCommunityChannelMessage}
 * (canonical doc + `id`/`channelId`). The typed twin of the Firestore listener's
 * `firestoreDocToMessage`, so both ingestion paths land on ONE shape.
 *
 * Every field is read off the TYPED `CommunityChannelMessageSearchItem` — no
 * duck typing. The only casts are single whole-blob bridges at the two spots
 * the generated API is nominally looser than the model: `content` (the API
 * types `content.links` as `object[]`, but the wire shape IS the canonical
 * `CommunityChannelMessageContent`) and the profile items (structurally the
 * model profile, different generated nominal type). ISO-string timestamps are
 * converted to `AppDate` via {@link toAppDate}.
 */
export function channelMessageSearchItemToMessage(
  channelId: string,
  item: CommunityChannelMessageSearchItem,
): ExtendedCommunityChannelMessage {
  return {
    id: item.id,
    channelId,
    clientMessageId: item.clientMessageId ?? item.id,
    rootMessageId: item.rootMessageId,
    parentMessageId: item.parentMessageId,
    level: item.level,
    userId: item.userId,
    profile: item.profile as unknown as CommunityChannelMemberProfile,
    subscriptionLevel:
      item.subscriptionLevel as ExtendedCommunityChannelMessage['subscriptionLevel'],
    content: item.content as unknown as CommunityChannelMessageContent,
    mentions: item.mentions as unknown as
      | CommunityChannelMessageMention[]
      | undefined,
    deleted: item.deleted,
    deletedByUserId: item.deletedByUserId,
    deletedAt: toAppDate(item.deletedAt),
    lastRepliedAt: toAppDate(item.lastRepliedAt),
    recentRepliedProfiles:
      item.recentRepliedProfiles as unknown as ExtendedCommunityChannelMessage['recentRepliedProfiles'],
    reactions: item.reactions,
    replyCount: item.replyCount,
    pinnedAt: toAppDate(item.pinnedAt),
    moderation:
      item.moderation as ExtendedCommunityChannelMessage['moderation'],
    createdAt: toAppDate(item.createdAt),
    updatedAt: toAppDate(item.updatedAt),
  };
}
